/**
 * Firebase initialization for Cloud Messaging (FCM) Web Push.
 *
 * Lazy-initialized on first getMessaging() call.
 * Returns null when required Firebase env config is missing (graceful degradation).
 */

import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getMessaging, type Messaging } from 'firebase/messaging'

let firebaseApp: FirebaseApp | null = null
let messaging: Messaging | null = null
let initAttempted = false

interface FirebaseWebConfig {
  apiKey: string
  authDomain: string
  projectId: string
  messagingSenderId: string
  appId: string
}

export function getFirebaseWebConfig(): FirebaseWebConfig | null {
  const apiKey = (import.meta.env.VITE_FIREBASE_API_KEY || '').trim()
  const authDomain = (import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '').trim()
  const projectId = (import.meta.env.VITE_FIREBASE_PROJECT_ID || '').trim()
  const messagingSenderId = (import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '').trim()
  const appId = (import.meta.env.VITE_FIREBASE_APP_ID || '').trim()

  if (!apiKey || !authDomain || !projectId || !messagingSenderId || !appId) {
    return null
  }

  return { apiKey, authDomain, projectId, messagingSenderId, appId }
}

export function getFirebaseMessaging(): Messaging | null {
  if (initAttempted) return messaging

  initAttempted = true
  const config = getFirebaseWebConfig()

  if (!config) {
    console.warn('[Firebase] Missing required config; FCM Web Push disabled.')
    return null
  }

  try {
    firebaseApp = initializeApp(config)
    messaging = getMessaging(firebaseApp)
    return messaging
  } catch (err) {
    console.error('[Firebase] Failed to initialize:', err)
    return null
  }
}
